import {
    addBreadcrumb,
    captureException,
    captureMessage,
} from '@sentry/browser'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isNull from 'lodash/isNull'

import {
    fetchPendingInvitationsSuccess,
    fetchUserFeaturePermissionsSuccess,
    fetchUserOrganizationFeaturePermissionsSuccess,
    loadAuthFailure,
    loadAuthFinish,
    loadAuthRequest,
    setOrganizationId,
    signInSuccess,
} from 'actions/auth'
import { fetchOrganizationSuccess } from 'actions/orgs'
import { setGlobalNotification } from 'actions/ui/app'
import {
    getUserOrganizationGroups,
    getUserOrganizations,
} from 'api/Cerebro/orgScoped/userApi'
import {
    CerebroPaginatedResponse,
    CerebroResourceResponse,
} from 'api/Cerebro/types'
import {
    ForgeSettings,
    getForgeSettings,
} from 'api/Cerebro/unscoped/appSettings'
import {
    getInvitations,
    getOrganization,
} from 'api/Cerebro/unscoped/organization'
import {
    getCurrentUser,
    getUserFeaturePermissions,
    getUserOrganizationFeaturePermissions,
    getUserOrganizationGroupFeaturePermissions,
    UserSettings,
} from 'api/Cerebro/unscoped/user'
import {
    cerebroApiRequest,
    cerebroApiRequestGuarded,
} from 'api/cerebroApiRequest'
import {
    userHasAssumeAnyUserPermissions,
    userHasCustomerServicePermissions,
} from 'helpers/featurePermissions'
import { getPath } from 'routing/pagePaths'
import { navigate } from 'routing/RoutingService'
import {
    selectDomainValue as selectAuthDomainValue,
    selectUserFeaturePermissions,
} from 'selectors/auth'
import { Organization, OrganizationGroup, User, UserPermission } from 'types'

import { isAuthenticated } from '../authTokens'
import {
    retryBackoffMs,
    throwIfRetryableResponse,
    unansweredReason,
    waitForRetryOpportunity,
} from '../connectivity'
import { setReconnecting } from '../reconnectStatus'
import { setDebugUserContext, setThirdPartyTracking } from '../thirdParty'

import { fetchOrganizationGroups } from './organizationGroups'
import { dispatch, getReduxState } from './reduxBridge'
import {
    fetchUserSettings,
    SettingPair,
    updateUserSettings,
} from './userSettings'

interface CurrentUserOptions {
    // Rethrow a request that got no answer (offline, server error) instead of
    // reporting "no user", so the caller can retry rather than treat a still
    // valid session as signed out.
    throwIfUnanswered?: boolean
}

/**
 * Fetch the current user's profile; returns null on error, and never throws
 * unless `throwIfUnanswered` is set.
 */
export async function fetchCurrentUser({
    throwIfUnanswered = false,
}: CurrentUserOptions = {}): Promise<CerebroResourceResponse<User> | null> {
    try {
        if (!isAuthenticated()) {
            return null
        }
        const response = await getCurrentUser()
        if (throwIfUnanswered) {
            throwIfRetryableResponse(response)
        }
        return response
    } catch (error) {
        if (throwIfUnanswered && unansweredReason(error)) {
            throw error
        }
        return null
    }
}

async function fetchUserFeaturePermissions(): Promise<
    CerebroResourceResponse<UserPermission[]>
> {
    return cerebroApiRequest(
        fetchUserFeaturePermissionsSuccess,
        getUserFeaturePermissions
    )
}

export async function fetchPendingInvitations(
    email: User['email']
): Promise<void> {
    await cerebroApiRequest(fetchPendingInvitationsSuccess, getInvitations, {
        state: 'pending',
        user_email: email,
    })
}

/**
 * Fetch feature permissions for the organization. For impersonating customer
 * service users, use the organization group's permissions instead (the user is
 * likely not a member of the organization).
 */
export async function fetchUserOrganizationFeaturePermissions({
    userFeaturePermissions,
    organizationId,
    organizationGroupId,
    shouldCommit,
}: {
    userFeaturePermissions?: UserPermission[]
    organizationId?: Organization['id']
    organizationGroupId?: OrganizationGroup['id']
    shouldCommit?: () => boolean
}): Promise<void> {
    const isImpersonating: boolean = selectAuthDomainValue(
        getReduxState(),
        'userIsImpersonating'
    )
    const isCustomerService = userFeaturePermissions
        ? userHasCustomerServicePermissions(userFeaturePermissions)
        : userHasCustomerServicePermissions(
              selectUserFeaturePermissions(getReduxState())
          )

    if (isImpersonating && isCustomerService) {
        const orgGroupId = isNull(organizationGroupId)
            ? selectAuthDomainValue(getReduxState(), 'organizationGroupId')
            : organizationGroupId

        await cerebroApiRequestGuarded(
            shouldCommit,
            fetchUserOrganizationFeaturePermissionsSuccess,
            getUserOrganizationGroupFeaturePermissions,
            orgGroupId
        )
    } else {
        await cerebroApiRequestGuarded(
            shouldCommit,
            fetchUserOrganizationFeaturePermissionsSuccess,
            getUserOrganizationFeaturePermissions,
            organizationId
        )
    }
}

/**
 * Signal that sign in has finished and set up debugging and third party tracking
 */
export async function saveCurrentUserToApp({
    currentUser,
    organization = null,
    organizationGroup = null,
    gilId,
}: {
    currentUser: User
    organization: Organization | null
    organizationGroup: OrganizationGroup | null
    gilId?: string
}): Promise<void> {
    setDebugUserContext(
        currentUser.username,
        currentUser.email,
        organization,
        organizationGroup
    )

    // Detached (the saga used `spawn`); its errors must not fail sign-in.
    void setThirdPartyTracking(currentUser, organization, gilId).catch(
        captureException
    )

    if (organization) {
        dispatch(fetchOrganizationSuccess(organization))
    }

    dispatch(
        signInSuccess({
            currentUser,
            organizationId: organization?.id,
            organizationGroupId: organizationGroup?.id,
        })
    )
}

async function getFallbackOrganizationGroup(): Promise<{
    organization: Organization | null
    organizationGroup: OrganizationGroup | null
}> {
    let organization = null
    let organizationGroup = null

    try {
        const {
            data: { results },
        }: CerebroPaginatedResponse<OrganizationGroup> =
            await getUserOrganizationGroups({ limit: 1 })
        if (!isEmpty(results)) {
            organization = get(results, ['0', 'organization'])
            organizationGroup = {
                ...get(results, ['0']),
                organization: undefined,
            }
        }
    } catch (err: any) {
        console.error(err)
    }

    return { organization, organizationGroup }
}

async function getFallbackOrganization(): Promise<Organization | null> {
    let organization = null

    try {
        const {
            data: { results },
        }: CerebroPaginatedResponse<Organization> = await getUserOrganizations({
            limit: 1,
        })
        if (!isEmpty(results)) {
            organization = get(results, ['0'])
        }
    } catch (err: any) {
        console.error(err)
    }

    return organization
}

async function getActiveOrganizationAndGroupForImpersonatingUser(): Promise<{
    organization: Organization | null
    organizationGroup: OrganizationGroup | null
}> {
    let organization = null
    let organizationGroup = null

    const storedOrgGroupId: number = selectAuthDomainValue(
        getReduxState(),
        'organizationGroupId'
    )

    let useFallbackOrgGroup = false
    if (!storedOrgGroupId) {
        useFallbackOrgGroup = true
    } else {
        // Impersonation users can query for any organization group
        const {
            data: { count, results },
        }: CerebroPaginatedResponse<OrganizationGroup> =
            await getUserOrganizationGroups({ id: storedOrgGroupId })

        if (count === 0) {
            useFallbackOrgGroup = true
        } else {
            organization = results[0].organization
            organizationGroup = {
                ...results[0],
                organization: undefined,
            } as unknown as OrganizationGroup
        }
    }

    if (useFallbackOrgGroup) {
        const {
            organization: fallbackOrganization,
            organizationGroup: fallbackOrganizationGroup,
        } = await getFallbackOrganizationGroup()

        organization = fallbackOrganization
        organizationGroup = fallbackOrganizationGroup
    }

    if (organization?.id) {
        try {
            const { data }: CerebroResourceResponse<Organization> =
                await getOrganization(organization.id)
            organization = data
        } catch (err: unknown) {
            console.error(err)
        }
    }

    return { organization, organizationGroup }
}

async function getActiveOrganizationForStandardUser(
    userSettings: UserSettings
): Promise<{
    organization: Organization | null
    organizationGroup: OrganizationGroup | null
}> {
    let organization: Organization | null = null

    const savedOrgId = userSettings.organizationId

    let useFallbackOrg = false
    if (!savedOrgId) {
        useFallbackOrg = true
    } else {
        try {
            const { data }: CerebroResourceResponse<Organization> =
                await getOrganization(savedOrgId)
            organization = data
        } catch {
            useFallbackOrg = true
        }
    }

    if (useFallbackOrg) {
        organization = await getFallbackOrganization()
    }

    const userSettingsUpdates: SettingPair[] = []
    if (
        organization &&
        userSettings.organizationId?.toString() !== organization.id?.toString()
    ) {
        userSettingsUpdates.push({
            path: ['organizationId'],
            value: organization.id,
        })
    }
    if (userSettings.organizationGroupId !== null) {
        userSettingsUpdates.push({
            path: ['organizationGroupId'],
            value: null,
        })
    }

    if (userSettingsUpdates.length > 0) {
        void updateUserSettings(userSettingsUpdates).catch(captureException)
    }

    return { organization, organizationGroup: null }
}

/**
 * Get the active organization and group for the current user, based on the
 * user's permissions and the current impersonation state.
 */
export async function getActiveOrganizationAndGroup(
    isImpersonating: boolean,
    userSettingsPromise: Promise<UserSettings>,
    userPermissionsPromise: Promise<
        CerebroResourceResponse<UserPermission[]>
    > | null = null
): Promise<{
    organization: Organization | null
    organizationGroup: OrganizationGroup | null
    userSettings: UserSettings
}> {
    let isStandardUser = true
    let organization = null
    let organizationGroup = null

    if (isImpersonating) {
        let userFeaturePermissions: UserPermission[]
        if (userPermissionsPromise) {
            const { data: permissions } = await userPermissionsPromise
            userFeaturePermissions = permissions
        } else {
            userFeaturePermissions = selectAuthDomainValue(
                getReduxState(),
                'userFeaturePermissions'
            )
        }

        const isCustomerService = userHasCustomerServicePermissions(
            userFeaturePermissions
        )

        if (isCustomerService) {
            isStandardUser = false
            const result =
                await getActiveOrganizationAndGroupForImpersonatingUser()
            organization = result.organization
            organizationGroup = result.organizationGroup
        }
    }

    if (isStandardUser) {
        const userSettings = await userSettingsPromise
        const result = await getActiveOrganizationForStandardUser(userSettings)
        organization = result.organization
        organizationGroup = result.organizationGroup
    }

    const userSettings = await userSettingsPromise
    return {
        organization,
        organizationGroup,
        userSettings,
    }
}

/**
 * Finish the sign-in process by fetching the authorized user, their feature
 * permissions, and their current organization.
 */
export async function tryFinishSignIn(
    currentUserOptions: CurrentUserOptions = {}
): Promise<void> {
    const result = await fetchCurrentUser(currentUserOptions)

    if (result?.status !== 200) {
        return
    }

    const currentUser = result.data

    // Kicked off together (deliberately not awaited yet) so they run in parallel.
    const userSettingsPromise = fetchUserSettings()
    const userPermissionsPromise = fetchUserFeaturePermissions()
    void fetchPendingInvitations(currentUser.email).catch(captureException)

    const isImpersonating: boolean = selectAuthDomainValue(
        getReduxState(),
        'userIsImpersonating'
    )

    const { organization, organizationGroup, userSettings } =
        await getActiveOrganizationAndGroup(
            isImpersonating,
            userSettingsPromise,
            userPermissionsPromise
        )

    const saveUserPromise = saveCurrentUserToApp({
        currentUser,
        organization,
        organizationGroup,
        gilId: userSettings.gilId,
    })

    const { data: userFeaturePermissions } = await userPermissionsPromise
    if (!organization) {
        // Send them to the auth organization page so they are assigned an organization
        if (!userHasAssumeAnyUserPermissions(userFeaturePermissions)) {
            navigate(getPath('authOrganizationPage'))
        }
    } else {
        void fetchOrganizationGroups(organization.id).catch(captureException)
        await fetchUserOrganizationFeaturePermissions({
            userFeaturePermissions,
            organizationId: organization.id,
            organizationGroupId: organizationGroup?.id,
        })
    }

    await saveUserPromise
}

export async function setActiveOrganization(
    organizationId: string
): Promise<void> {
    await fetchUserOrganizationFeaturePermissions({ organizationId })

    await updateUserSettings([
        { path: ['organizationId'], value: organizationId },
    ])

    dispatch(setOrganizationId({ organizationId }))
}

export const bootstrapRetryTiming = {
    retryBaseMs: 2_000,
    retryMaxMs: 30_000,
}

// loadAuth reruns whenever sign-in state changes; only the latest run may keep
// retrying or clear the reconnecting state.
let latestLoadAuthRun = 0

async function loadAuthOnce(isLatestRun: () => boolean): Promise<void> {
    const {
        displayDowntimePage,
        displayMaintenancePage,
        globalNotification,
    }: ForgeSettings = await getForgeSettings()
    if (!isLatestRun()) {
        return
    }
    dispatch(setGlobalNotification(globalNotification))

    if (displayMaintenancePage || displayDowntimePage) {
        dispatch(
            loadAuthFinish({ displayMaintenancePage, displayDowntimePage })
        )
        return
    }

    await tryFinishSignIn({ throwIfUnanswered: true })

    if (isLatestRun()) {
        dispatch(loadAuthFinish({ displayMaintenancePage }))
    }
}

/**
 * Authentication entry point for the application. A bootstrap request that
 * gets no answer (offline, server error) is retried, showing the reconnecting
 * page meanwhile, so a dropped connection never strands the user on an error
 * page or the sign-in form with a valid session. Any other failure is final.
 */
export async function loadAuth(): Promise<void> {
    latestLoadAuthRun += 1
    const run = latestLoadAuthRun
    const isLatestRun = (): boolean => run === latestLoadAuthRun
    dispatch(loadAuthRequest())

    // Tallied per reason: an outage can last hours.
    const failureReasons: Record<string, number> = {}
    let failureCount = 0
    try {
        for (;;) {
            try {
                await loadAuthOnce(isLatestRun)
                if (failureCount > 0 && isLatestRun()) {
                    captureMessage(
                        'App bootstrap recovered after going unanswered',
                        {
                            level: 'info',
                            extra: { failureReasons },
                        }
                    )
                }
                return
            } catch (err: any) {
                const reason = unansweredReason(err)
                if (!reason) {
                    captureException(err)
                    // A newer run owns the auth state; don't show its error page.
                    if (!isLatestRun()) {
                        return
                    }
                    dispatch(loadAuthFailure({ error: err.message }))
                    throw err
                }
                if (!isLatestRun()) {
                    return
                }
                failureCount += 1
                failureReasons[reason] = (failureReasons[reason] ?? 0) + 1
                addBreadcrumb({
                    category: 'auth',
                    level: 'warning',
                    message: 'App bootstrap got no answer',
                    data: { reason, failureCount },
                })
                setReconnecting(true)
                await waitForRetryOpportunity(
                    retryBackoffMs(failureCount, bootstrapRetryTiming)
                )
                if (!isLatestRun()) {
                    return
                }
            }
        }
    } finally {
        if (isLatestRun()) {
            setReconnecting(false)
        }
    }
}
